import i18n from "i18next";

import { initReactI18next } from "react-i18next";

import {
  asSupportedLanguage,
  chosenLanguage,
  FALLBACK_LANGUAGE,
} from "shared/language";

// Import translation files
import admin from "assets/locales/en/admin.json";
import buAdmin from "assets/locales/en/buAdmin.json";
import buIcons from "assets/locales/en/buIcons.json";
import common from "assets/locales/en/common.json";
import components from "assets/locales/en/components.json";
import endUser from "assets/locales/en/endUser.json";
import error from "assets/locales/en/error.json";
import general from "assets/locales/en/general.json";
import groupLegal from "assets/locales/en/groupLegal.json";
import help from "assets/locales/en/help.json";
import importNs from "assets/locales/en/import.json";
import login from "assets/locales/en/login.json";
import navigation from "assets/locales/en/navigation.json";
import notFound from "assets/locales/en/notFound.json";
import adminDe from "assets/locales/de/admin.json";
import buAdminDe from "assets/locales/de/buAdmin.json";
import buIconsDe from "assets/locales/de/buIcons.json";
import commonDe from "assets/locales/de/common.json";
import errorDe from "assets/locales/de/error.json";
import notFoundDe from "assets/locales/de/notFound.json";
import groupLegalDe from "assets/locales/de/groupLegal.json";
import helpDe from "assets/locales/de/help.json";
import endUserDe from "assets/locales/de/endUser.json";
import importNsDe from "assets/locales/de/import.json";
import navigationDe from "assets/locales/de/navigation.json";

const namespacesToPreload: string[] = [
  "general",
  "components",
  "login",
  "common",
  "navigation",
  "endUser",
  "buAdmin",
  "buIcons",
  "admin",
  "groupLegal",
  "help",
  "import",
];

/** The last language rendered in this browser. Not a preference — i18next
 *  writes it on every `languageChanged`, this module's own init included — so
 *  it is read as a starting point and never as a decision. */
function readStoredLanguage(): string | null {
  try {
    return localStorage.getItem("i18nextLng");
  } catch {
    return null;
  }
}

i18n.use(initReactI18next).init({
  fallbackLng: ["en"],
  debug: false,
  returnObjects: true,
  interpolation: {
    escapeValue: false,
  },
  resources: {
    en: {
      admin,
      buAdmin,
      buIcons,
      common,
      components,
      endUser,
      error,
      general,
      groupLegal,
      help,
      import: importNs,
      login,
      navigation,
      notFound,
    },
    // German bundles exist only for these namespaces; fallbackLng covers
    // the rest (components, general, login) in English.
    //
    // `error` and `notFound` are here because the not-found page is reachable:
    // it is the router's `defaultNotFoundComponent` (NAV-86 deleted an address
    // outright, so a stale bookmark lands on it), and it was the one surface a
    // German reader could reach and find written in English.
    de: {
      admin: adminDe,
      buAdmin: buAdminDe,
      buIcons: buIconsDe,
      common: commonDe,
      error: errorDe,
      groupLegal: groupLegalDe,
      help: helpDe,
      endUser: endUserDe,
      import: importNsDe,
      navigation: navigationDe,
      notFound: notFoundDe,
    },
  },
  // Where the *first paint* lands, before anything is known about who is
  // looking: their own choice if they have made one, otherwise the language
  // last rendered here, which keeps a reload from flickering. `i18nextLng` is
  // the same storage key i18next-browser-languagedetector uses (and the source
  // app relies on), so this survives reloads without the dependency.
  //
  // Deliberately *not* where a business unit's default is applied (PRD H7):
  // the unit is not known until the user's profile and BU document have
  // resolved, long after this module runs. `useBusinessUnitLanguage` applies it
  // then, and only to somebody who has not chosen — so a reader with no choice
  // of their own can see one language for the first beat and their business unit's for
  // the rest, which is the price of not blocking the whole app on a Firestore
  // read.
  lng:
    chosenLanguage() ??
    asSupportedLanguage(readStoredLanguage()) ??
    FALLBACK_LANGUAGE,
  ns: namespacesToPreload,
  defaultNS: "general",
});

i18n.on("languageChanged", (lng) => {
  try {
    localStorage.setItem("i18nextLng", lng);
  } catch {
    // Storage can be unavailable outright (a browser blocking site data). The
    // language still applies for this session.
  }
});

export default i18n;
